import { useEffect, useLayoutEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Icon } from '../components/Icon.tsx';
import { useAuthStore } from '../store/auth.ts';
import { useReveal } from '../features/landing/useReveal.ts';

/**
 * Public /security — an English security & data-protection statement written for
 * the technical evaluator (an IT/security person at a council or vendor). It is
 * deliberately English + LTR (the rest of the public site is Dhivehi/RTL), and
 * set in the same Material 3 language as the rest of Office: tonal surfaces,
 * sentence-case labels, generous spacing. Every checkable claim links to the
 * check.
 */

const REVIEWED = 'August 2026';

const VERIFY = [
  { name: 'Qualys SSL Labs', what: 'TLS / transport', href: 'https://www.ssllabs.com/ssltest/analyze.html?d=office.wapplify.app' },
  { name: 'securityheaders.com', what: 'HTTP security headers', href: 'https://securityheaders.com/?q=office.wapplify.app&followRedirects=on' },
  { name: 'Mozilla Observatory', what: 'Web hardening', href: 'https://developer.mozilla.org/en-US/observatory/analyze?host=office.wapplify.app' },
];

const DATA = [
  {
    term: 'One secure sign-in',
    body: 'A single account across every app. Passwords are hashed with bcrypt — we never store them in a form we could read. Sessions are short-lived and can be revoked instantly; sign out once and you are signed out everywhere.',
  },
  {
    term: 'Each council is walled off',
    body: 'Every council is a separate tenant. One council can never see another’s residents, documents, or records. That boundary is enforced on every single request, not just hidden in the interface.',
  },
  {
    term: 'Encrypted in transit',
    body: 'Every connection is encrypted end to end. Traffic runs through Cloudflare, and the TLS setup is independently rated A+ (see above).',
  },
  {
    term: 'Least privilege by default',
    body: 'Each app can reach only its own data. The databases are not exposed to the internet, and every account and service is scoped as tightly as the work allows.',
  },
];

const BUILD = [
  'The platform is audited against the OWASP Top 10 — the standard list of how web apps get attacked.',
  'It’s built on an ORM (Prisma) that parameterizes database queries by default, which rules out the most common class of injection attacks; the few hand-written queries still pass every user value as a bound parameter.',
  'The codebase is covered by around 1,500 automated tests.',
  'Our encryption and security headers are graded by independent public scanners — the ones you can run yourself above.',
];

const FAQ = [
  {
    q: 'Can Wapplify staff read our data?',
    a: 'Like any hosted service, our team has the administrative access needed to run and support the system. We only look at your data when it’s necessary to operate the service or help you with a problem, and we never share it. To be upfront: we’re not a “zero-knowledge” product, and we’d rather you hear that clearly from us.',
  },
  {
    q: 'Do you sell our data, share it, or use it to train AI?',
    a: 'No. Your data is used for one thing: running the service for you. We don’t sell it, we don’t give it to advertisers, and we don’t feed it into AI training.',
  },
  {
    q: 'What happens to our data if we stop using Wapplify?',
    a: 'It’s yours. Ask us and we’ll export it for you in a usable format, and delete it from our systems on request.',
  },
  {
    q: 'How often is data backed up — and can it actually be recovered?',
    a: 'Every night, encrypted, and copied off-site. We don’t just trust that backups work; we regularly restore them to prove it. Recovering from a bad change or an accidental deletion is routine, not a scramble.',
  },
  {
    q: 'Is our data encrypted?',
    a: 'Every connection is encrypted in transit (TLS, rated A+), and our off-site backups are encrypted so that even the storage provider can’t read them. If you have a specific requirement around encryption at rest, please get in touch and we’ll talk it through.',
  },
  {
    q: 'Who in our council can see what?',
    a: 'Access is role-based and controlled by your own admins. You decide who’s in and what they can do, and access can be revoked instantly — signing someone out takes effect across every app at once.',
  },
  {
    q: 'What happens if there’s a security incident?',
    a: 'If an incident affected your data, we’d tell you promptly and plainly — what happened, what we’re doing about it, and what you should do — and work to contain and fix it. We’d always choose to over-communicate rather than leave you guessing.',
  },
];

const SECURITY_EMAIL = 'mohamedirufaam@gmail.com';

function Eyebrow({ children }: { children: React.ReactNode }): JSX.Element {
  return <p className="text-sm font-medium text-primary">{children}</p>;
}

export function Security(): JSX.Element {
  // Scroll-reveal (fade + rise) for the page's blocks — same system as the
  // landing. Targets the `id="top"` root below to add the `reveal-on` gate.
  useReveal();

  // English + LTR + light for this page; restore the visitor's chrome on leave.
  // useLayoutEffect (not useEffect) so this matches useMarketingChrome's effect
  // phase — otherwise, navigating security→landing, this passive cleanup would
  // fire AFTER the landing's layout effect and clobber dir back to ltr.
  useLayoutEffect(() => {
    window.scrollTo(0, 0); // open at the top, not wherever the previous page was scrolled
    const html = document.documentElement;
    const prevDir = html.getAttribute('dir') ?? 'ltr';
    const prevLang = html.getAttribute('lang') ?? 'en';
    const hadDark = html.classList.contains('dark');
    html.setAttribute('dir', 'ltr');
    html.setAttribute('lang', 'en');
    html.classList.remove('dark');
    return () => {
      html.setAttribute('dir', prevDir);
      html.setAttribute('lang', prevLang);
      if (hadDark) html.classList.add('dark');
    };
  }, []);

  // Live status published by the automated checks (see /root/monitoring). Absent
  // or stale-safe: if the file is missing the line simply doesn't render.
  const [lastPassed, setLastPassed] = useState<string | null>(null);
  useEffect(() => {
    fetch('/status.json', { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d && typeof d.lastPassed === 'string') setLastPassed(d.lastPassed);
      })
      .catch(() => {});
  }, []);
  const passedLabel = (() => {
    if (!lastPassed) return null;
    const d = new Date(lastPassed);
    if (Number.isNaN(d.getTime())) return null;
    const date = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    return `${date} at ${time}`;
  })();

  return (
    <div id="top" dir="ltr" className="min-h-screen bg-background text-foreground overflow-x-clip">
      {/* Masthead — letterhead. Only for public (logged-out) visitors; signed-in
          users already have the app chrome around this page. */}
      {!useAuthStore.getState().accessToken && (
        <header className="mx-auto flex max-w-[72rem] items-center justify-between px-6 pt-8">
          <a href="/" className="flex items-center gap-2.5">
            <img src="/icons/wapoffice-mark.svg" alt="" className="size-8" />
            <span className="text-lg font-medium">Wapplify Office</span>
          </a>
          <Link to="/login" className="btn-ghost">
            Sign in
          </Link>
        </header>
      )}

      <main className="mx-auto max-w-[72rem] px-6 pb-24">
        {/* Title block */}
        <div className="reveal mt-14 pt-8">
          <Eyebrow>Security &amp; data protection</Eyebrow>
          <h1 className="mt-4 text-[2.6rem] font-normal leading-[1.05] sm:text-[3.1rem]">
            How we keep council data safe
          </h1>
          <p className="mt-5 max-w-[54ch] text-lg leading-relaxed text-muted-foreground">
            Wapplify Office holds things that matter and are hard to replace — resident records, signed
            documents, meeting minutes. This page explains, in plain terms, how we look after them — and points
            you to checks you can run yourself.
          </p>
          <p className="mono-meta mt-6 text-muted-foreground">
            office.wapplify.app &middot; Last reviewed {REVIEWED}
          </p>
        </div>

        {/* Opening */}
        <section className="reveal mt-16 space-y-5 text-[1.02rem] leading-relaxed">
          <p>
            We build the tools councils use every day — signing, meetings, the census register, procurement.
            That means you trust us with information that’s hard to replace, and we don’t take that lightly. This
            page is our attempt to be open about how we look after it.
          </p>
          <p>
            If you’re the person responsible for reviewing that, this is written for you. Wherever something can
            be verified independently, we’ve put the link right next to it, so you can confirm it yourself.
          </p>
        </section>

        {/* Independent verification — the signature block */}
        <section className="reveal mt-16 pt-10">
          <Eyebrow>Independently verified</Eyebrow>
          <h2 className="mt-3 text-3xl font-normal">See for yourself</h2>
          <p className="mt-4 max-w-[58ch] leading-relaxed text-muted-foreground">
            Our encryption and transport security are graded by public scanners that anyone can run. Here’s where
            things stand today, with links so you can check them yourself.
          </p>

          {/* The mark */}
          <div className="mt-7 flex items-center justify-between gap-6 rounded-[var(--radius)] bg-[hsl(var(--surface-container-low))] p-6">
            <div>
              <p className="text-sm font-medium">Qualys SSL Labs</p>
              <p className="mt-1 text-sm text-muted-foreground">TLS &amp; transport security rating</p>
              <p className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium" style={{ color: 'hsl(var(--success))' }}>
                <span
                  aria-hidden
                  className="inline-block size-1.5 rounded-full"
                  style={{ background: 'hsl(var(--success))' }}
                />
                Verified {REVIEWED}
              </p>
            </div>
            <div
              className="leading-none"
              style={{ fontFamily: 'var(--font-thaana-heading)', color: 'hsl(var(--success))', fontSize: '3.4rem' }}
            >
              A+
            </div>
          </div>

          {passedLabel && (
            <div className="mt-6 flex items-center gap-2.5 rounded-xl bg-[hsl(var(--success-container))] px-4 py-3 text-[hsl(var(--on-success-container))]">
              <span className="relative flex size-2.5">
                <span
                  className="absolute inline-flex size-full rounded-full opacity-60 motion-safe:animate-ping"
                  style={{ background: 'hsl(var(--success))' }}
                />
                <span
                  className="relative inline-flex size-2.5 rounded-full"
                  style={{ background: 'hsl(var(--success))' }}
                />
              </span>
              <span className="text-[0.95rem] font-medium">
                Automated checks last passed {passedLabel}
              </span>
            </div>
          )}

          <p className="mt-6 text-sm text-muted-foreground">Run the checks yourself:</p>
          <ul className="mt-3 overflow-hidden rounded-[var(--radius)] bg-[hsl(var(--surface-container-low))]">
            {VERIFY.map((v) => (
              <li key={v.name} className="border-b border-border/40 last:border-b-0">
                <a
                  href={v.href}
                  target="_blank"
                  rel="noreferrer"
                  className="group flex items-center justify-between gap-4 px-5 py-4 transition-colors hover:bg-black/[0.04] hover:text-primary dark:hover:bg-white/[0.05]"
                >
                  <span>
                    <span className="font-medium">{v.name}</span>
                    <span className="ml-2 text-sm text-muted-foreground">{v.what}</span>
                  </span>
                  <Icon
                    name="open_in_new"
                    size={18}
                    className="shrink-0 text-primary transition-transform group-hover:translate-x-0.5"
                  />
                </a>
              </li>
            ))}
          </ul>
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
            Alongside TLS, every page is served with a full set of modern browser security headers — HSTS with
            preload, a content-security policy, and more — which the tools above grade too.
          </p>
        </section>

        {/* Your data */}
        <section className="reveal mt-16 pt-10">
          <Eyebrow>Your data</Eyebrow>
          <h2 className="mt-3 text-3xl font-normal">How it’s protected</h2>
          <dl className="mt-7 space-y-7">
            {DATA.map((d) => (
              <div key={d.term} className="grid gap-x-8 gap-y-1.5 sm:grid-cols-[13rem_1fr]">
                <dt className="text-lg font-medium text-foreground">{d.term}</dt>
                <dd className="leading-relaxed text-muted-foreground">{d.body}</dd>
              </div>
            ))}
          </dl>
        </section>

        {/* Where it runs */}
        <section className="reveal mt-16 pt-10">
          <Eyebrow>Infrastructure</Eyebrow>
          <h2 className="mt-3 text-3xl font-normal">Where it runs</h2>
          <p className="mt-4 max-w-[62ch] leading-relaxed text-muted-foreground">
            Wapplify Office runs on managed cloud infrastructure behind Cloudflare, which filters hostile traffic
            and absorbs denial-of-service attacks before they reach us. The servers are firewalled so only the
            website is reachable from the internet — the databases are not. Admin access is limited to a small
            team, the software is kept up to date, and our encrypted off-site backups mean we can recover quickly
            if we ever need to.
          </p>
        </section>

        {/* Backups */}
        <section className="reveal mt-16 pt-10">
          <Eyebrow>If the worst happens</Eyebrow>
          <h2 className="mt-3 text-3xl font-normal">Backups, and how we test them</h2>
          <div className="mt-4 space-y-4 leading-relaxed text-muted-foreground">
            <p>
              Every night, all data — and the signed documents themselves — are backed up, encrypted, and copied
              off-site to storage we don’t run ourselves. So a problem on our end can’t take the backups down with
              it.
            </p>
            <p>
              A backup is only useful if it restores, so we regularly restore ours to make sure they work. For
              you, that means a bad change or an accidental deletion should be a quick recovery.
            </p>
          </div>
        </section>

        {/* How we build */}
        <section className="reveal mt-16 pt-10">
          <Eyebrow>How we work</Eyebrow>
          <h2 className="mt-3 text-3xl font-normal">How the software is built</h2>
          <ul className="mt-6 space-y-2">
            {BUILD.map((b) => (
              <li
                key={b}
                className="flex gap-3 rounded-xl bg-[hsl(var(--surface-container-low))] px-5 py-4 leading-relaxed"
              >
                <span
                  aria-hidden
                  className="mt-2.5 size-2 flex-none rounded-full"
                  style={{ background: 'hsl(var(--primary))' }}
                />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* FAQ */}
        <section className="reveal mt-16 pt-10">
          <Eyebrow>Common questions</Eyebrow>
          <h2 className="mt-3 text-3xl font-normal">Security &amp; data, answered</h2>
          <div className="mt-7 space-y-3">
            {FAQ.map((f) => (
              <div
                key={f.q}
                className="rounded-[var(--radius)] bg-[hsl(var(--surface-container-low))] p-5 sm:p-6"
              >
                <h3 className="text-xl font-medium">{f.q}</h3>
                <p className="mt-2.5 max-w-[62ch] leading-relaxed text-muted-foreground">{f.a}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Honest limits */}
        <section className="reveal mt-16 pt-10">
          <Eyebrow>Being straight with you</Eyebrow>
          <h2 className="mt-3 text-3xl font-normal">What we haven’t done — yet</h2>
          <div className="mt-4 space-y-4 leading-relaxed text-muted-foreground">
            <p>
              We’re a small, focused team, and we don’t currently hold formal certifications like SOC&nbsp;2 or
              ISO&nbsp;27001 — those come from dedicated external audits we haven’t gone through yet. We’d rather
              be honest about where we are.
            </p>
            <p>
              What we do is build to the same principles those standards describe, and keep things independently
              checkable — which is why the links above are here. If there’s a requirement we don’t meet yet,
              please tell us; we’d genuinely like to know.
            </p>
          </div>
        </section>

        {/* Contact */}
        <section className="reveal mt-16 pt-10">
          <Eyebrow>Responsible disclosure</Eyebrow>
          <h2 className="mt-3 text-3xl font-normal">Found something? We’d like to hear it</h2>
          <p className="mt-4 max-w-[58ch] leading-relaxed text-muted-foreground">
            If you think you’ve found a security issue, please email us. We read every report, we’re grateful for
            them, and we’ll work with you to get it sorted.
          </p>
          <a
            href={`mailto:${SECURITY_EMAIL}?subject=${encodeURIComponent('Wapplify Office — security report')}`}
            dir="ltr"
            className="btn-secondary mt-5"
          >
            {SECURITY_EMAIL}
          </a>
        </section>

        {/* Colophon */}
        <footer className="reveal mt-20 border-t border-border/50 pt-6">
          <p className="mono-meta text-muted-foreground">
            Reflects our posture as of {REVIEWED}
          </p>
        </footer>
      </main>
    </div>
  );
}
